import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import api from "@/lib/api";

function FooterSection({ title, children }) {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const on = () => setIsDesktop(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return (
    <details open={isDesktop || undefined} className="footer-section group border-b border-[color:var(--ink-hairline)] pb-3 md:border-0 md:pb-0">
      <summary className="flex cursor-pointer list-none items-center justify-between font-editorial italic text-[color:var(--ink-muted)] md:mb-4">
        {title}
        <ChevronDown className="footer-chevron h-4 w-4 transition-transform group-open:rotate-180 md:hidden" />
      </summary>
      <ul className="footer-collapsible mt-3 space-y-2 text-[14px] text-[color:var(--ink-body)] md:mt-0">{children}</ul>
    </details>
  );
}

export default function Footer() {
  const { isAuthenticated } = useAuth();
  const { branding, publicConfig } = useBranding();
  const [legal, setLegal] = useState([]);
  const [tagline, setTagline] = useState("");
  useEffect(() => {
    api.get("/legal").then((r) => setLegal(r.data || [])).catch(() => {});
    api.get("/pages/landing").then((r) => setTagline(r.data?.footer_tagline || "")).catch(() => {});
  }, []);
  return (
    <footer data-testid="site-footer" className="border-t border-[color:var(--ink-hairline)] bg-[color:var(--paper-soft)]">
      <div className="mx-auto grid max-w-7xl gap-6 px-5 py-12 md:grid-cols-5 md:gap-12 md:py-16 lg:px-8">
        <div className="md:col-span-2">
          <img src={branding.logo_url || "/logo.png"} alt={branding.site_name || "HK ForexEdge"} className="h-9 w-auto" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.png"; }} />
          <p className="mt-4 max-w-sm text-[14px] leading-relaxed text-[color:var(--ink-body)]">
            {tagline || "Account-bound trading instruments, curated signals, and gated courses — for traders who take execution seriously."}
          </p>
        </div>

        <FooterSection title="The catalogue">
          <li><Link to="/store/indicators" className="hover:text-[color:var(--evergreen)]" data-testid="footer-indicators">Indicators</Link></li>
          <li><Link to="/store/expert-advisors" className="hover:text-[color:var(--evergreen)]" data-testid="footer-expert-advisors">Expert Advisors</Link></li>
          {publicConfig?.signals_enabled !== false && (
            <li><Link to="/signals" className="hover:text-[color:var(--evergreen)]" data-testid="footer-signals">Signals</Link></li>
          )}
          <li><Link to="/store/courses" className="hover:text-[color:var(--evergreen)]" data-testid="footer-courses">Courses</Link></li>
          <li><Link to="/bundles" className="hover:text-[color:var(--evergreen)]" data-testid="footer-bundles">Bundles</Link></li>
        </FooterSection>

        <FooterSection title="Company">
          <li><Link to="/about" className="hover:text-[color:var(--evergreen)]" data-testid="footer-about">About</Link></li>
          <li><Link to="/contact" className="hover:text-[color:var(--evergreen)]" data-testid="footer-contact">Contact</Link></li>
          <li><Link to="/verify" className="hover:text-[color:var(--evergreen)]" data-testid="footer-verify">Verify certificate</Link></li>
          <li><Link to="/affiliate" className="hover:text-[color:var(--evergreen)]" data-testid="footer-affiliate">Affiliate program</Link></li>
          {!isAuthenticated && (
            <>
              <li><Link to="/login" className="hover:text-[color:var(--evergreen)]" data-testid="footer-login">Sign in</Link></li>
              <li><Link to="/register" className="hover:text-[color:var(--evergreen)]" data-testid="footer-register">Create account</Link></li>
            </>
          )}
        </FooterSection>

        <FooterSection title="Legal">
          {legal.map((d) => (
            <li key={d.slug}><Link to={`/legal/${d.slug}`} className="hover:text-[color:var(--evergreen)]" data-testid={`footer-legal-${d.slug}`}>{d.title}</Link></li>
          ))}
        </FooterSection>
      </div>
      <div className="border-t border-[color:var(--ink-hairline)]">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-5 py-6 text-xs text-[color:var(--ink-muted)] md:flex-row md:items-center lg:px-8">
          <span>© {new Date().getFullYear()} HK Forexedge. All rights reserved.</span>
          <span className="font-editorial italic">Est. 2019</span>
        </div>
      </div>
    </footer>
  );
}
